import { UI_LANGUAGES } from "@/i18n/languages";
import { useTranslation } from "react-i18next";
import { useState, useRef, useEffect } from "react";
import { Globe, Sun, Moon, Monitor, ChevronDown } from "lucide-react";
import { useTheme } from "@/hooks/use-theme";
import {
  useLanguage,
  type LanguageCode,
} from "@/store/use-ui-preferences-store";
import { cn } from "@/lib/utils";

const languages = UI_LANGUAGES;



export default function LanguageThemeSwitcher() {
  const { t } = useTranslation();
  const themes = [
    { code: "system", icon: <Monitor className="h-4 w-4" />, label: t("settings.appearance.systemPreference") },
    { code: "light", icon: <Sun className="h-4 w-4" />, label: t("common.lightMode") },
    { code: "dark", icon: <Moon className="h-4 w-4" />, label: t("common.darkMode") },
  ];
  const { mode, setTheme } = useTheme();
  const { language, setLanguage } = useLanguage();
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const languageDropdownRef = useRef<HTMLDivElement>(null);

  const currentLanguage =
    languages.find((lang) => lang.code === language) || languages[0];

  const handleLanguageChange = (code: LanguageCode) => {
    setLanguage(code); // Local değerleri set et (API'ye gönderme)
    setIsLanguageOpen(false);
  };

  const handleThemeChange = (newTheme: string) => {
    setTheme(newTheme as "light" | "dark" | "system"); // Local değerleri set et (API'ye gönderme)
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        languageDropdownRef.current &&
        !languageDropdownRef.current.contains(event.target as Node)
      ) {
        setIsLanguageOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="flex items-center justify-between w-full">
      {/* Language Selector - Dropdown */}
      <div className="relative" ref={languageDropdownRef}>
        <button
          onClick={() => setIsLanguageOpen(!isLanguageOpen)}
          className="flex items-center gap-2 px-2 py-1 text-sm font-medium transition-colors hover:text-foreground"
        >
          <Globe className="h-4 w-4 text-muted-foreground" />
          <span className="text-muted-foreground">{currentLanguage.label}</span>
          <ChevronDown
            className={cn(
              "h-3 w-3 text-muted-foreground transition-transform duration-200",
              isLanguageOpen && "rotate-180"
            )}
          />
        </button>

        {/* Dropdown Menu */}
        {isLanguageOpen && (
          <div className="absolute top-full left-0 mt-1 w-[180px] bg-white dark:bg-gray-800 border border-border-primary dark:border-border-primary rounded-lg shadow-lg z-50 overflow-hidden">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={cn(
                  "w-full px-3 py-2 flex items-center gap-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors",
                  language === lang.code && "bg-gray-50 dark:bg-gray-700"
                )}
              >
                <span className="text-base">{lang.flag}</span>
                <span className="text-sm font-medium text-text-primary dark:text-text-primary">
                  {lang.label} - {lang.fullLabel}
                </span>
                {language === lang.code && (
                  <div className="ml-auto w-2 h-2 rounded-full bg-blue-500" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Theme Selector */}
      <div className="flex items-center gap-1">
        {themes.map((theme) => (
          <button
            key={theme.code}
            onClick={() => handleThemeChange(theme.code)}
            className={`p-1.5 rounded-md transition-colors disabled:opacity-50 ${
              mode === theme.code
                ? "text-primary"
                : "text-muted-foreground hover:text-foreground"
            }`}
            title={theme.label}
            aria-label={`Switch to ${theme.label} theme`}
          >
            {theme.icon}
          </button>
        ))}
      </div>
    </div>
  );
}
